import {
  COOKIE_CONSENT_CHANGED_EVENT,
  COOKIE_CONSENT_STORAGE_KEY,
  COOKIE_CONSENT_VERSION,
} from '@/lib/analytics/cookie-consent';

export const DEFAULT_GTM_ID = 'GTM-N5TZHW3L';
export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID?.trim() || DEFAULT_GTM_ID;

/**
 * Standard bootstrap loads gtm.js from googletagmanager.com (below).
 * If Google Tag Diagnostics shows "Your site does not use a supported CDN", the property likely has
 * [Google tag gateway for advertisers](https://developers.google.com/tag-platform/tag-manager/gateway)
 * turned on without Cloudflare/Akamai/GCP forwarding. Fix in GA4/Google Ads/GTM: disable gateway, or
 * complete gateway setup on a supported CDN — do not proxy GTM through an unsupported host only.
 */

// GA4 is the direct Google tag. The GTM container still owns the Ads tag.
export const GTM_LINKED_TAGS = {
  ga4: 'G-157C4L9QQE',
  googleAds: 'AW-11345026393',
} as const;

export const GTM_ORIGIN = 'https://www.googletagmanager.com';

/** Direct Google tag (gtag.js) for the GA4 measurement id. GTM still loads separately for Ads. */
export function googleTagScriptSrc(measurementId: string = GTM_LINKED_TAGS.ga4): string {
  return `${GTM_ORIGIN}/gtag/js?id=${encodeURIComponent(measurementId)}`;
}

/**
 * Queues the Google tag config. Page views stay off here so private routes are
 * not tracked; public navigations send `page_view` from the page tracker.
 */
export function googleAnalyticsConfigScript(measurementId: string = GTM_LINKED_TAGS.ga4): string {
  return `gtag('js', new Date());gtag('config', '${measurementId}', { send_page_view: false });`;
}

export const GTM_NOSCRIPT_SRC = `${GTM_ORIGIN}/ns.html?id=${GTM_ID}`;
export const GTM_SCRIPT_SRC = `${GTM_ORIGIN}/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
export const GTM_SCRIPT_SELECTOR = 'script[data-gtm-bootstrap]';
export { COOKIE_CONSENT_CHANGED_EVENT, COOKIE_CONSENT_STORAGE_KEY };

// Denied worldwide until the banner records a current v2 consent. Kleaners is an EU
// controller; do not default-grant outside the EEA. security_storage stays granted.
export const GTM_CONSENT_DEFAULT_SCRIPT = `
window.dataLayer = window.dataLayer || [];
window.gtag = window.gtag || function gtag(){window.dataLayer.push(arguments);};
window.gtag('consent', 'default', {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted',
  wait_for_update: 2000
});
try {
  var stored = localStorage.getItem('${COOKIE_CONSENT_STORAGE_KEY}');
  if (stored) {
    var c = JSON.parse(stored);
    if (c && c.version === '${COOKIE_CONSENT_VERSION}') {
      var on = function (v) { return v ? 'granted' : 'denied'; };
      window.gtag('consent', 'update', {
        analytics_storage: on(c.performance),
        ad_storage: on(c.marketing),
        ad_user_data: on(c.adsMeasurement),
        ad_personalization: on(c.adsPersonalization),
        functionality_storage: on(c.preferences),
        personalization_storage: on(c.adsPersonalization),
        security_storage: 'granted'
      });
    }
  }
} catch (e) {}
`;

export function isGoogleTagManagerInjected(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  return Boolean(document.querySelector(GTM_SCRIPT_SELECTOR));
}

function ensureGtmPreconnect(): void {
  if (document.querySelector('link[data-gtm-preconnect]')) {
    return;
  }

  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = GTM_ORIGIN;
  preconnect.crossOrigin = 'anonymous';
  preconnect.dataset.gtmPreconnect = 'true';
  document.head.appendChild(preconnect);
}

/**
 * Inject gtm.js once. Consent Mode defaults (denied) must already be in the
 * document head so the container cannot store ads/analytics cookies until the
 * banner grants consent via `gtag('consent', 'update', …)`.
 */
export function loadGoogleTagManager(nonce?: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (isGoogleTagManagerInjected()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });

  ensureGtmPreconnect();

  const script = document.createElement('script');
  script.async = true;
  script.src = GTM_SCRIPT_SRC;
  script.dataset.gtmBootstrap = 'true';
  if (nonce) {
    script.nonce = nonce;
  }
  document.head.appendChild(script);
}

/**
 * Install the GTM container on every page so Tag Assistant / Google install
 * checks can see `GTM-*`. Storage stays denied until the cookie banner updates
 * Consent Mode (handled by `pushConsentToGtag`).
 */
export function bindGoogleTagManagerToConsent(nonce?: string): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  loadGoogleTagManager(nonce);
  return () => undefined;
}

/** @deprecated Use bindGoogleTagManagerToConsent. */
export function scheduleGoogleTagManager(nonce?: string): () => void {
  return bindGoogleTagManagerToConsent(nonce);
}
